import { useCallback } from 'react';
import { routes } from '~/constants';
import { useIoContext, useOverlayContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { sendErrReport } from '~/infra/utils';
const CALLBACK_PATH_BY_PROVIDER = {
    qbo: '/qbo-integration/oauth-callback',
    xero: '/xero-integration/oauth-callback'
};
export function useNativeOAuthSignIn(provider) {
    const { browser: { changeLocation, scrubUrlFragment }, native: { isNative, openSystemBrowser }, cookies, localStorage } = useIoContext();
    const { showErrorToast } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const providerErrorMessage = provider === 'qbo' ? commonTexts.toasts.failedToContinueWithIntuit() : commonTexts.toasts.failedToContinueWithXero();
    const showProviderErrorToast = useCallback(()=>{
        showErrorToast(providerErrorMessage, "native-oauth-".concat(provider, "-error-toast"));
    }, [
        provider,
        providerErrorMessage,
        showErrorToast
    ]);
    const handleCallbackResult = useCallback(async (result)=>{
        const { clearPendingOAuthState, readPendingOAuthState } = await import('~/native/oauth-state-storage');
        if (result.kind === 'ignored') {
            clearPendingOAuthState(localStorage, provider);
            sendErrReport(new Error("native-oauth-callback-ignored: ".concat(provider)));
            showProviderErrorToast();
            return;
        }
        if (result.kind === 'error') {
            clearPendingOAuthState(localStorage, result.provider);
            sendErrReport(new Error("native-oauth-callback-error: ".concat(result.error)));
            showProviderErrorToast();
            return;
        }
        const expectedState = readPendingOAuthState(localStorage, result.provider);
        if (expectedState == null || expectedState !== result.state) {
            clearPendingOAuthState(localStorage, result.provider);
            sendErrReport(new Error("native-oauth-state-mismatch: ".concat(result.provider)));
            showProviderErrorToast();
            return;
        }
        try {
            cookies.setAuthTokenForNative(result.authToken);
        } catch (err) {
            clearPendingOAuthState(localStorage, result.provider);
            sendErrReport(err);
            showProviderErrorToast();
            return;
        }
        clearPendingOAuthState(localStorage, result.provider);
        scrubUrlFragment();
        changeLocation(routes.home);
    }, [
        localStorage,
        provider,
        cookies,
        scrubUrlFragment,
        changeLocation,
        showProviderErrorToast
    ]);
    const dispatchOAuth = useCallback(async (authUrl)=>{
        if (!isNative) {
            changeLocation(authUrl);
            return;
        }
        const { persistPendingOAuthState, clearPendingOAuthState } = await import('~/native/oauth-state-storage');
        const { parseOAuthCallback } = await import('~/native/oauth-callback-listener');
        let backendState;
        let callbackHost;
        try {
            backendState = new URL(authUrl).searchParams.get('state');
            callbackHost = new URL(__BASE_URL__).host;
        } catch (err) {
            sendErrReport(err);
            showProviderErrorToast();
            return;
        }
        if (backendState == null) {
            sendErrReport(new Error("native-oauth-no-state-in-intuit-url: ".concat(provider)));
            showProviderErrorToast();
            return;
        }
        persistPendingOAuthState(localStorage, provider, backendState);
        const callbackPath = CALLBACK_PATH_BY_PROVIDER[provider];
        try {
            const result = await openSystemBrowser(authUrl, callbackHost, callbackPath);
            await handleCallbackResult(parseOAuthCallback(result));
        } catch (err) {
            clearPendingOAuthState(localStorage, provider);
            const code = err === null || err === void 0 ? void 0 : err.code;
            if (code !== 'USER_CANCELED') {
                sendErrReport(err);
                showProviderErrorToast();
            }
        }
    }, [
        isNative,
        localStorage,
        provider,
        openSystemBrowser,
        handleCallbackResult,
        changeLocation,
        showProviderErrorToast
    ]);
    return {
        isNative,
        dispatchOAuth
    };
}
